import React from 'react';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import HomeIcon from '@material-ui/icons/Home';
import BusinessIcon from '@material-ui/icons/Business';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import ListIcon from '@material-ui/icons/List';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import Button from '@material-ui/core/Button';
import { convertMilitaryStringToAmPm, getServiceEntity } from '../helper';
import dataProvider from '../dataProvider';

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'absolute',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: '4px',
    padding: theme.spacing(2, 2, 3),
  },
  textRow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  icon: {
    width: '33px',
    height: '33px',
    padding: '4px',
    borderRadius: '100%',
    boxSizing: 'border-box',
    display: 'inline',
    color: '#043454',
    marginRight: '10px',
    border: '1px solid #eaeaea',
  },
  link: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#043454',
    color: 'white',
    borderRadius: '4px',
    padding: '4px 12px',
    margin: '4px',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#054672',
      color: 'white',
      textDecoration: 'none',
    },
  },
}));

const AppointmentModal = ({
  info,
  appointment,
  open,
  handleClose,
  setShowRescheduleModal,
  setScheduledWork,
}) => {
  const classes = useStyles();
  const getModalStyle = () => {
    const top = 50;
    const left = 50;

    return {
      top: `${top}%`,
      left: `${left}%`,
      transform: `translate(-${top}%, -${left}%)`,
    };
  };

  if (!appointment) {
    return null;
  }

  const fetchRecord = async (id) => {
    try {
      await dataProvider
        .getOne('scheduled-work', {
          id,
        })
        .then(({ data }) => {
          setScheduledWork(data);
        });
    } catch (error) {
      console.error(error);
    }
  };

  const address =
    appointment.address.one_line_human_readable_address ||
    appointment.client_location.short_name;

  const totalLaborHours = Number.isInteger(appointment.total_labor_hours)
    ? appointment.total_labor_hours
    : appointment.total_labor_hours.toFixed(1);

  const totalLaborHoursPostfix = totalLaborHours === 1 ? 'hr' : 'hrs';

  const services = appointment.services
    .filter((s) => !s.is_upsell || s.upsell_status === 'approved')
    .filter((s) => s.status !== 'DELETED');

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="appointment-modal"
      aria-describedby="appointment-information"
    >
      <div style={getModalStyle()} className={classes.paper}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eaeaea',
            marginBottom: '12px',
          }}
        >
          <h2>{info?.event.title}</h2>
          <span>
            {convertMilitaryStringToAmPm(
              appointment.confirmed_appointment_start_time,
            )}{' '}
            -{' '}
            {convertMilitaryStringToAmPm(
              appointment.expected_appointment_end_time,
            )}
          </span>
        </header>
        <main>
          <div>
            <div className={classes.textRow}>
              <Tooltip
                title={`${getServiceEntity()}: ${
                  appointment.service_vehicle_name
                }`}
                aria-label={`${getServiceEntity()}: ${
                  appointment.service_vehicle_name
                }`}
              >
                <LocalShippingIcon className={classes.icon} />
              </Tooltip>
              <span>{appointment.service_vehicle_name}</span>
            </div>
            <div className={classes.textRow}>
              <Tooltip
                title={`Address: ${address}`}
                aria-label={`Address: ${address}`}
              >
                {appointment.address?.one_line_human_readable_address ? (
                  <HomeIcon className={classes.icon} />
                ) : (
                  <BusinessIcon className={classes.icon} />
                )}
              </Tooltip>
              <span>{address}</span>
            </div>
            {appointment.vehicle.id && (
              <div className={classes.textRow}>
                <Tooltip
                  title={`Vehicle: ${appointment.vehicle.display_name}`}
                  aria-label={`Vehicle: ${appointment.vehicle.display_name}`}
                >
                  <DirectionsCarIcon className={classes.icon} />
                </Tooltip>
                <span>{appointment.vehicle.display_name}</span>
              </div>
            )}
            <div className={classes.textRow}>
              <Tooltip title="Services" aria-label="Services">
                <ListIcon className={classes.icon} />
              </Tooltip>
              <div style={{ fontWeight: 'bold' }}>
                {`Services - ${totalLaborHours}${totalLaborHoursPostfix}`}
              </div>
            </div>
            <ul style={{ marginLeft: '20px' }}>
              {services.length > 0 ? (
                services.map((service) => (
                  <li key={service.service_id}>{`${service.display_name}`}</li>
                ))
              ) : (
                <li>No scheduled services</li>
              )}
            </ul>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Link
                href={`#/scheduled-work/${appointment.scheduled_work_id}/show`}
                variant="contained"
                className={classes.link}
              >
                <OpenInNewIcon style={{ marginRight: '5px' }} />
                View Appointment
              </Link>
              <Button
                variant="contained"
                className={classes.link}
                onClick={async () => {
                  handleClose();
                  setShowRescheduleModal(true);
                  await fetchRecord(appointment.scheduled_work_id);
                }}
              >
                <CalendarIcon style={{ marginRight: '5px' }} />
                Reschedule
              </Button>
            </div>
          </div>
        </main>
      </div>
    </Modal>
  );
};

export default AppointmentModal;
